// Migration from the legacy `privateData.*` shape (companyName, address,
// city, postalCode, taxIdentificationNumber, ...) to the DAC7-ready
// `privateData.tax` shape defined in docs/dac7-private-data-schema.md.
//
// The migration is idempotent: it only runs when `tax` is absent or has
// `schemaVersion < 1`. Saving from the new form upgrades the record to 1.

const { isHetuPlain, isYtunnusPlain } = require('./taxInfoSchema');

const SCHEMA_VERSION_MIGRATED = 0; // best-effort, awaiting user confirmation
const SCHEMA_VERSION_CONFIRMED = 1; // user has submitted the new form

const toIso = dateLike => {
  if (!dateLike) return null;
  if (typeof dateLike === 'string') {
    // already ISO or partial — accept ISO YYYY-MM-DD only
    const m = dateLike.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
  }
  // legacy birthday-input shape: { year, month, day }
  const { year, month, day } = dateLike;
  if (!year || !month || !day) return null;
  const yy = String(year).padStart(4, '0');
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

// Birthlocation comes from a LocationAutocompleteInputField whose value
// looks like { search, selectedPlace: { address, origin } }. We only have
// the formatted address string to work with — try to extract a city /
// country pair conservatively.
const parseBirthLocation = birthLocation => {
  if (!birthLocation) return { city: null, country: 'FI' };
  if (typeof birthLocation === 'string') {
    return { city: birthLocation, country: 'FI' };
  }
  const address = birthLocation.selectedPlace?.address || birthLocation.search || null;
  if (!address) return { city: null, country: 'FI' };
  // Address is usually "City, Country" — take the first comma-separated chunk
  // as the city and the last as the country.
  const parts = address.split(',').map(s => s.trim()).filter(Boolean);
  if (parts.length === 1) return { city: parts[0], country: 'FI' };
  return { city: parts[0], country: 'FI' };
};

// Split a single legal name into first/last for an IndividualSeller.
// Last word becomes lastName, the rest becomes firstName.
const splitName = legalName => {
  if (!legalName || typeof legalName !== 'string') {
    return { firstName: null, lastName: null };
  }
  const parts = legalName.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: null };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

// Build the new shape from the legacy fields. Does not encrypt — that
// must happen server-side on the next save.
const migrateLegacyTaxInfo = (privateData = {}, protectedData = {}, profile = {}) => {
  const accountRole = profile?.publicData?.account_role || null;
  const isCompanyAbroad = !!privateData?.isCompanyAbroad;

  let identifierType;
  let plaintext = null;
  let issuedBy = 'FI';
  let nameForm;
  let companyForm = null;

  if (accountRole === 'freelancer') {
    nameForm = 'ENTITY';
    identifierType = 'YTUNNUS';
    // Older records may have a businessRegistrationNumber that holds a
    // Y-tunnus, but in the freelancer flow the canonical place is the
    // VAT-deductible business id stored under privateData.companyName. We
    // don't have a Y-tunnus in legacy records, so leave it unset and
    // require the user to confirm on next visit.
    plaintext = isYtunnusPlain(privateData?.businessRegistrationNumber)
      ? privateData.businessRegistrationNumber
      : null;
  } else if (accountRole === 'light-entrepreneur' && isCompanyAbroad) {
    nameForm = 'ENTITY';
    identifierType = 'OTHER_TIN';
    plaintext = privateData?.taxIdentificationNumber || null;
    issuedBy = privateData?.tinCountry?.selectedPlace?.address || privateData?.tinCountry || 'FI';
    if (typeof issuedBy === 'string' && issuedBy.length > 2) {
      // user-friendly country name → leave as null and prompt
      issuedBy = 'FI';
    }
  } else if (accountRole === 'light-entrepreneur') {
    nameForm = 'INDIVIDUAL';
    if (isHetuPlain(privateData?.taxIdentificationNumber || '')) {
      identifierType = 'HETU';
      plaintext = privateData.taxIdentificationNumber;
    } else {
      identifierType = null;
    }
  } else {
    nameForm = 'INDIVIDUAL';
    identifierType = null;
  }

  const { firstName: profileFirst, lastName: profileLast } = {
    firstName: profile?.firstName || null,
    lastName: profile?.lastName || null,
  };
  const splitFromCompany = splitName(privateData?.companyName);
  const firstName =
    nameForm === 'INDIVIDUAL'
      ? profileFirst || splitFromCompany.firstName
      : null;
  const lastName =
    nameForm === 'INDIVIDUAL'
      ? profileLast || splitFromCompany.lastName
      : null;
  const legalName = nameForm === 'ENTITY' ? privateData?.companyName || null : null;

  const birthInfoDate = toIso(privateData?.dateOfBirth);
  const birthInfoLoc = parseBirthLocation(privateData?.birthLocation);
  const hasBirthInfo = !!birthInfoDate || !!birthInfoLoc.city;

  const tax = {
    schemaVersion: SCHEMA_VERSION_MIGRATED,
    residency: { countryCodes: isCompanyAbroad ? [] : ['FI'] },
    identifier: identifierType
      ? {
          type: identifierType,
          encrypted: null,
          plaintext,
          last4: plaintext ? plaintext.slice(-4) : null,
          issuedBy,
        }
      : null,
    vat: protectedData?.vat_number || null,
    name: {
      form: nameForm,
      firstName,
      lastName,
      legalName,
    },
    address: {
      street: privateData?.address || null,
      postcode: privateData?.postalCode || null,
      city: privateData?.city || null,
      country: 'FI',
      buildingIdentifier: null,
      suiteIdentifier: null,
    },
    birthInfo: hasBirthInfo
      ? { date: birthInfoDate, city: birthInfoLoc.city, country: birthInfoLoc.country }
      : null,
    companyForm,
    businessRegistration:
      privateData?.businessRegistrationNumber
        ? {
            number: privateData.businessRegistrationNumber,
            countryOfIssue:
              typeof privateData?.businessRegistrationCountryOfIssue === 'string'
                ? privateData.businessRegistrationCountryOfIssue
                : null,
          }
        : null,
    permanentEstablishments: [],
    submittedAt: null,
    verifiedAt: null,
  };

  return tax;
};

// Idempotent wrapper that returns the existing record unchanged if it is
// already at the current schema version.
const ensureTaxInfo = (privateData = {}, protectedData = {}, profile = {}) => {
  const existing = privateData?.tax;
  if (existing && existing.schemaVersion >= SCHEMA_VERSION_CONFIRMED) {
    return existing;
  }
  if (existing && existing.schemaVersion === SCHEMA_VERSION_MIGRATED) {
    return existing;
  }
  return migrateLegacyTaxInfo(privateData, protectedData, profile);
};

module.exports = {
  migrateLegacyTaxInfo,
  ensureTaxInfo,
  SCHEMA_VERSION_MIGRATED,
  SCHEMA_VERSION_CONFIRMED,
};
